import { Flex } from '@chakra-ui/react';
import { Label } from '@mesh.ai/rabbit';
import { useDispatch } from 'react-redux';
import {
  setPeerFormFillingScoreSettings,
  usePeerFormFillingScoreSettings,
  useReviewCreationData,
} from 'reducers/reviewCreationSlice';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';

import { Accordion } from 'uiToolkitV1/Accordion';
import { AccordionButton, AccordionItem2, AccordionPanel2 } from 'uiToolkitV1/Accordion/Accordion';
import { Checkbox } from 'uiToolkitV1/Checkbox';
import { ListWarning } from 'uiToolkitV1/ListWarning';
import { TooltipInfoV1 } from 'uiToolkitV1/TooltipInfoV1';
import { TextBodyMedium, TextHeader4 } from 'uiToolkitV1/Typography';

import {
  SELF_ANSWER_VISIBILITY_FOR_PEERS_VALUES,
  SELF_ANSWER_VISIBILITY_FOR_PEERS_VALUES_LABELS,
  useTemplateMetadata,
} from '../../Hooks/dataHooks/formFillingsDataHooks';
import { PreviewFormButton } from './PreviewFormButton';

const PeerAndUpwardWarning = ({ feedbackFormId }: { feedbackFormId: number }) => {
  const { reviewCycleId } = useReviewCreationData();
  const peerMetadata = useTemplateMetadata({
    reviewCycleId,
    feedbackFormId,
    providerType: REVIEW_PROVIDER_TYPES.PEER,
  });
  const upwardMetadata = useTemplateMetadata({
    reviewCycleId,
    feedbackFormId,
    providerType: REVIEW_PROVIDER_TYPES.UPWARD,
  });

  if (!peerMetadata && !upwardMetadata) {
    return null;
  }

  let warningText = '';
  if (peerMetadata && upwardMetadata) {
    warningText = 'Peer and upward reviewer will not be able to see the overall scores';
  } else if (peerMetadata) {
    warningText = 'Peer reviewer will not be able to see the overall scores';
  } else {
    warningText = 'Upward reviewer will not be able to see the overall scores';
  }

  return <ListWarning mt={2} warningText={warningText} />;
};

export const PeerReviewerSettings = ({ feedbackFormId }: { feedbackFormId: number }) => {
  const { reviewCycleId } = useReviewCreationData();
  const dispatch = useDispatch();
  const peerFormFillingScoreSettings = usePeerFormFillingScoreSettings({ feedbackFormId });
  const selfAnswerVisibilityForPeers = peerFormFillingScoreSettings.selfAnswerVisibilityForPeers ?? [];

  const peerTemplateMetadata = useTemplateMetadata({
    reviewCycleId,
    feedbackFormId,
    providerType: REVIEW_PROVIDER_TYPES.PEER,
  });

  const upwardTemplateMetadata = useTemplateMetadata({
    reviewCycleId,
    feedbackFormId,
    providerType: REVIEW_PROVIDER_TYPES.UPWARD,
  });

  const hasPeerReviewer = !!peerTemplateMetadata;
  const hasUpwardReviewer = !!upwardTemplateMetadata;
  const peerTemplateHasQuestions = peerTemplateMetadata?.template?.questionCount > 0;
  const upwardTemplateHasQuestions = upwardTemplateMetadata?.template?.questionCount > 0;

  if (!hasPeerReviewer && !hasUpwardReviewer) return null;

  const handleChange = ({ type, isChecked }) => {
    let updatedVisibility = Array.isArray(selfAnswerVisibilityForPeers) ? [...selfAnswerVisibilityForPeers] : [];

    if (isChecked) {
      if (!updatedVisibility.includes(type)) {
        updatedVisibility.push(type);
      }
    } else {
      updatedVisibility = updatedVisibility.filter((item) => item !== type);
    }

    dispatch(
      setPeerFormFillingScoreSettings({
        feedbackFormId,
        settings: { selfAnswerVisibilityForPeers: updatedVisibility },
      })
    );
  };

  let headingText = '';
  if (hasPeerReviewer && hasUpwardReviewer) {
    headingText = 'Peer Reviewer/Upward Reviewer';
  } else if (hasPeerReviewer) {
    headingText = 'Peer Reviewer';
  } else {
    headingText = 'Upward Reviewer';
  }

  return (
    <Accordion defaultIndex={0} allowMultiple allowToggle boxShadow="none !important">
      <AccordionItem2 sxProps={{ borderBottom: 'none !important' }}>
        <AccordionButton>
          <Flex justifyContent="space-between" alignItems="center" flexGrow={1}>
            <TextHeader4 bold>{headingText}</TextHeader4>
            <Flex justifyContent="flex-end" alignItems="center" flexGrow={1}>
              {hasPeerReviewer && (
                <PreviewFormButton
                  feedbackFormId={feedbackFormId}
                  providerType={REVIEW_PROVIDER_TYPES.PEER}
                  isDisabled={!peerTemplateHasQuestions}
                />
              )}
              {hasUpwardReviewer && (
                <PreviewFormButton
                  feedbackFormId={feedbackFormId}
                  providerType={REVIEW_PROVIDER_TYPES.UPWARD}
                  isDisabled={!upwardTemplateHasQuestions}
                />
              )}
            </Flex>
          </Flex>
        </AccordionButton>
        <AccordionPanel2 pl="32px !important" mb={5}>
          {hasPeerReviewer && (
            <Flex flexDir="column" gap={3} mb={3}>
              <Flex alignItems="center" gap={2}>
                <TextBodyMedium>
                  Can peer see self answers while filling out their forms? Select the sections they can view
                </TextBodyMedium>
                <TooltipInfoV1 title="Peer reviewers can view the employee's answers if the self-review forms have been submitted" />
              </Flex>
              <Flex gap="80px">
                {Object.values(SELF_ANSWER_VISIBILITY_FOR_PEERS_VALUES).map((type) => (
                  <Checkbox
                    key={type}
                    id={`peer-${type.toLowerCase()}`}
                    isChecked={selfAnswerVisibilityForPeers.includes(type)}
                    // @ts-expect-error onChange type not defined properly in checkbox component
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { checked } = e.target;
                      handleChange({
                        type,
                        isChecked: checked,
                      });
                    }}
                    labelComp={
                      <Label htmlFor={`peer-${type.toLowerCase()}`}>
                        {SELF_ANSWER_VISIBILITY_FOR_PEERS_VALUES_LABELS[type]}
                      </Label>
                    }
                  />
                ))}
              </Flex>
            </Flex>
          )}
          <PeerAndUpwardWarning feedbackFormId={feedbackFormId} />
        </AccordionPanel2>
      </AccordionItem2>
    </Accordion>
  );
};
